import { HTTP_STATUS, isApiError, type AuthTokens } from '@brightlocal/api-client';

import { hasIdentityChanged } from './identityClaims';
import { saveJWT, clearJWT, getJWT } from './manageJWT';
import { markSessionExpired } from './sessionExpiredError';

let refreshPromise: Promise<AuthTokens> | null = null;

interface TokenRefreshServiceConfig {
  /** API call to exchange the httpOnly cookie for a new access token. */
  refreshTokenApi: () => Promise<AuthTokens>;
  /** Called when token refresh fails and the session is invalidated. */
  onSessionExpired: () => void;
  /**
   * Called when a refresh returns a token issued for a different identity than
   * the previous one — a Login As / Switch back happened on the old platform.
   */
  onIdentityChanged?: () => void;
}

let config: TokenRefreshServiceConfig | null = null;

/** Must be called once at app bootstrap before any refresh can happen. */
export const initTokenRefreshService = (serviceConfig: TokenRefreshServiceConfig) => {
  config = serviceConfig;
};

/**
 * Ends the current session: clears tokens and notifies the configured
 * `onSessionExpired` handler (which performs the logout redirect).
 * The notification no-ops if the service was not yet initialized —
 * unreachable in practice, since apiClient init runs at bootstrap.
 */
export const expireSession = () => {
  clearJWT();
  config?.onSessionExpired();
};

/**
 * Refresh the access token. If multiple calls happen at once, only one request is made and shared.
 * If successful, saves the new tokens.
 *
 * Rejection handling: a 401 ends the session immediately. A 403 (the endpoint's
 * response to a consumed/invalid refresh token) is retried once, immediately —
 * the httpOnly refresh cookie is shared browser-wide, so if a competing refresh
 * (another tab) rotated it, the retry picks up the new cookie; a transient 403
 * was also observed recovering on immediate retry. If the retry doesn't succeed
 * — whatever it fails with — the original 403 is conclusive. Either way the
 * session is expired (tokens cleared + onSessionExpired) and the rejection is
 * rethrown tagged via `markSessionExpired`, so route guards can distinguish it
 * from ordinary 401/403s on other endpoints. Other errors (network, 5xx)
 * propagate untagged without ending the session.
 */
export const refreshAccessToken = (): Promise<AuthTokens> => {
  if (refreshPromise) return refreshPromise;

  const pending = (async () => {
    try {
      if (!config) {
        throw new Error('tokenRefreshService not initialized — call initTokenRefreshService first');
      }

      const { refreshTokenApi, onIdentityChanged } = config;
      const previousAccessToken = getJWT()?.accessToken ?? null;

      const persistTokens = (tokens: AuthTokens) => {
        saveJWT(tokens);

        if (hasIdentityChanged(previousAccessToken, tokens.accessToken)) {
          onIdentityChanged?.();
        }

        return tokens;
      };

      try {
        const tokens = await refreshTokenApi();

        return persistTokens(tokens);
      } catch (error: unknown) {
        if (!isApiError(error)) {
          throw error;
        }

        if (error.status === HTTP_STATUS.FORBIDDEN) {
          try {
            const tokens = await refreshTokenApi();

            return persistTokens(tokens);
          } catch {
            // The retry failed — the original 403 is the conclusive rejection
            expireSession();
            throw markSessionExpired(error);
          }
        }

        if (error.status === HTTP_STATUS.AUTHORIZATION_ERROR) {
          expireSession();
          throw markSessionExpired(error);
        }

        throw error;
      }
    } finally {
      refreshPromise = null;
    }
  })();

  refreshPromise = pending;

  return pending;
};
